import axios from 'axios';

export const api = axios.create({
  baseURL: '/api/v1',
  withCredentials: true,
  headers: { 'Content-Type': 'application/json' },
});

api.interceptors.response.use(
  (res) => {
    // Apr 23, 2026 (Bug 7) — auto-refresh credit chip whenever any AI feature
    // or an explicit /credits/deduct call succeeds. Keeps TopBar in sync
    // without every call site having to remember to bump the bus.
    try {
      const method = (res.config?.method ?? '').toLowerCase();
      const url = res.config?.url ?? '';
      const isMutating = method === 'post' || method === 'put' || method === 'patch';
      if (isMutating && (url.startsWith('/ai/') || url.includes('/credits/deduct'))) {
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('aura:credits:refresh'));
        }
      }
    } catch {
      // Ignore — this is best-effort UX sugar, not critical.
    }
    return res;
  },
  (error) => {
    if (error.response?.status === 401) {
      // Redirect to login if not already there
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }

    // 402 — Kiaan subscription required. Dispatch a custom event so the AIPanel
    // can render the KiaanLockedCard without every call site needing to handle it.
    if (
      error.response?.status === 402 &&
      error.response?.data?.error === 'kiaan_subscription_required'
    ) {
      try {
        const payload = error.response.data;
        window.dispatchEvent(
          new CustomEvent('aura:kiaan:locked', {
            detail: {
              error: payload.error,
              message: payload.message ?? 'Subscribe to Kiaan to unlock AI features.',
              ctaUrl: payload.ctaUrl ?? 'https://kiaan.ai',
              ctaLabel: payload.ctaLabel ?? 'Explore Kiaan',
            },
          })
        );
      } catch {
        // Best-effort — don't break the error chain.
      }
    }

    return Promise.reject(error);
  }
);

// ── Manufacturing / Production planning helpers ─────────────────────────────
// Thin wrappers so pages don't hard-code endpoints. Safe to evolve as the
// backend adds bucketed responses — we fall back to client-side splitting
// when the API still returns a flat list.

export type PlanningBucket = 'today' | 'tomorrow' | 'thisWeek' | 'backlog';

export async function getProductionPlan(params?: { bucket?: PlanningBucket }) {
  const res = await api.get('/manufacturing-flow/planning', {
    params: params?.bucket ? { bucket: params.bucket } : undefined,
  });
  return res.data?.data ?? res.data;
}

export async function startShift(payload: { orderIds?: string[]; productionOrderIds?: string[] } = {}) {
  // Backend contract: POST /manufacturing-p0/shift/start { productionOrderIds: string[] }
  const productionOrderIds =
    payload.productionOrderIds ?? payload.orderIds ?? [];
  const res = await api.post('/manufacturing-p0/shift/start', { productionOrderIds });
  return res.data?.data ?? res.data;
}
